import { useEffect, useMemo, useState } from "react";
import dayjs from "@shared/dayjs";
import Button from "@shared/v2/Button";
import IconButton from "@shared/v2/IconButton";
import { CopyRegularV6, PencilSolidV6, PlusSolidV6, TrashCanSolidV6 } from "@shared/v2/Icomoon";
import { TBody, TD, TH, THead, TR, Table } from "../../Reports/components/BusinessDashboard/Table/Table";
import TemplatesPagination from "../TemplatesPagination";
import TemplatesToolbar from "../TemplatesToolbar";
import { THEAD_CLASSES } from "../types";
import useTemplateSearchParams from "../useTemplateSearchParams";
import { useMessagingServiceUrl } from "../../reducers/layoutReducer/selectors";
import TextTemplateModal from "./TextTemplateModal";
import DeleteTextTemplateModal from "./DeleteTextTemplateModal";
import {
  errorMessage,
  getTextTemplates,
  type Pagination as PaginationData,
  type TextTemplate,
  type TextTemplateDraft,
} from "./service";

const NEW_TEMPLATE: TextTemplateDraft = { templateName: "", template: "" };
const DATE_FORMAT = "MMM DD, YYYY";

type SortKey = "dateCreated" | "dateUpdated" | "templateName";
type SortDirection = "asc" | "desc";

// Last Updated falls back to the created date for templates that were never edited
const sortValue = (template: TextTemplate, key: SortKey) =>
  dayjs(
    key === "dateUpdated" ? (template.dateUpdated ?? template.dateCreated) : template.dateCreated,
  ).valueOf();

const TextTemplates = () => {
  const { page, urlSearch, search, setSearch, updateParams } = useTemplateSearchParams();
  const messagingServiceUrl = useMessagingServiceUrl() as string;

  const [templates, setTemplates] = useState<TextTemplate[]>([]);
  const [pagination, setPagination] = useState<Pick<PaginationData, "currentPage" | "totalPages">>({
    currentPage: 1,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editTemplate, setEditTemplate] = useState<TextTemplateDraft | null>(null);
  const [deleteTemplate, setDeleteTemplate] = useState<TextTemplate | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; direction: SortDirection } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getTextTemplates(messagingServiceUrl, { page, search: urlSearch }, controller.signal)
      .then((data) => {
        setTemplates(data.textTemplates);
        setPagination(data.pagination);
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(errorMessage(err));
        setLoading(false);
      });
    return () => controller.abort();
  }, [messagingServiceUrl, page, urlSearch, refreshKey]);

  const refresh = () => setRefreshKey((key) => key + 1);

  // The messaging service has no sort option, so this orders the loaded page only
  const sortedTemplates = useMemo(() => {
    if (!sort) return templates;
    const modifier = sort.direction === "asc" ? 1 : -1;
    return [...templates].sort((a, b) => (sortValue(a, sort.key) - sortValue(b, sort.key)) * modifier);
  }, [templates, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((prev) =>
      prev?.key === key && prev.direction === "asc" ? { key, direction: "desc" } : { key, direction: "asc" },
    );

  const onDuplicate = (template: TextTemplate) =>
    setEditTemplate({ templateName: "", template: template.template });

  const onDeleted = () => {
    setDeleteTemplate(null);
    // Step back a page if we just removed the last item on it
    if (templates.length === 1 && page > 1) updateParams({ page: page - 1 });
    else refresh();
  };

  const isEmpty = !loading && !error && templates.length === 0;

  return (
    <div className="tw-flex tw-flex-col tw-gap-[16px] tw-flex-1 tw-min-h-0">
      <TemplatesToolbar
        search={search}
        onSearchChange={setSearch}
        loading={loading}
        createLabel="Create New Text Template"
        onCreate={() => setEditTemplate(NEW_TEMPLATE)}
      />

      {error && <div className="tw-text-brivity-coral-100">{error}</div>}

      {isEmpty && urlSearch && (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-[8px] tw-py-[48px] tw-text-gray-50">
          <span className="tw-text-18d tw-font-semibold">No results found</span>
          <span>No text templates match &quot;{urlSearch}&quot;.</span>
        </div>
      )}

      {isEmpty && !urlSearch && (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-[16px] tw-py-[48px] tw-text-gray-50">
          <span className="tw-text-18d tw-font-semibold">Create your first Text Template</span>
          <span>Save time by reusing messages you send often.</span>
          {/* @ts-expect-error props */}
          <Button
            className="tw-flex tw-items-center tw-gap-[6px]"
            size="medium"
            onClick={() => setEditTemplate(NEW_TEMPLATE)}
          >
            <PlusSolidV6 />
            Create New Text Template
          </Button>
        </div>
      )}

      {templates.length > 0 && (
        <div className="tw-flex-1 tw-overflow-auto">
          <Table className="tw-border-separate">
            <THead className="tw-bg-white tw-sticky tw-top-0 tw-z-10">
              {/* @ts-expect-error props */}
              <TR>
                {/* @ts-expect-error props */}
                <TH
                  className={THEAD_CLASSES}
                  sort={sort?.key === "templateName" && sort.direction}
                  onSort={() => toggleSort("templateName")}
                >
                  Title
                </TH>
                {/* @ts-expect-error props */}
                <TH className={`tw-w-full ${THEAD_CLASSES}`}>Message</TH>
                {/* @ts-expect-error props */}
                <TH
                  className={`tw-text-center ${THEAD_CLASSES}`}
                  sort={sort?.key === "dateCreated" && sort.direction}
                  onSort={() => toggleSort("dateCreated")}
                >
                  Created
                </TH>
                {/* @ts-expect-error props */}
                <TH
                  className={`tw-text-center ${THEAD_CLASSES}`}
                  sort={sort?.key === "dateUpdated" && sort.direction}
                  onSort={() => toggleSort("dateUpdated")}
                >
                  Last Updated
                </TH>
                {/* @ts-expect-error props */}
                <TH className={`tw-text-center ${THEAD_CLASSES}`} />
              </TR>
            </THead>
            {/* @ts-expect-error props */}
            <TBody>
              {sortedTemplates.map((template) => (
                // @ts-expect-error props
                <TR key={template.uuid} className="tw-border-0 hover:tw-bg-gray-5">
                  {/* @ts-expect-error props */}
                  <TD className="tw-max-w-[200px] tw-truncate" title={template.templateName}>
                    {template.templateName}
                  </TD>
                  {/* @ts-expect-error props */}
                  <TD className="tw-max-w-0 tw-truncate" title={template.template}>
                    {template.template}
                  </TD>
                  {/* @ts-expect-error props */}
                  <TD className="tw-text-center tw-whitespace-nowrap">
                    {template.isShared ? (
                      <span className="tw-flex tw-px-[8px] tw-py-[4px] tw-rounded-[4px] tw-bg-theme-brand-lighter tw-text-theme-brand-dark">
                        Brivity Template
                      </span>
                    ) : (
                      dayjs(template.dateCreated).format(DATE_FORMAT)
                    )}
                  </TD>
                  {/* @ts-expect-error props */}
                  <TD className="tw-text-center tw-whitespace-nowrap">
                    {dayjs(template.dateUpdated ?? template.dateCreated).format(DATE_FORMAT)}
                  </TD>
                  {/* @ts-expect-error props */}
                  <TD>
                    <div className="tw-flex tw-gap-[4px] tw-justify-center tw-items-center">
                      {/* @ts-expect-error props */}
                      <IconButton
                        aria-label="Edit"
                        schema="tertiary"
                        onClick={() => setEditTemplate(template)}
                      >
                        <PencilSolidV6 />
                      </IconButton>
                      {/* @ts-expect-error props */}
                      <IconButton
                        aria-label="Duplicate"
                        schema="tertiary"
                        onClick={() => onDuplicate(template)}
                      >
                        <CopyRegularV6 />
                      </IconButton>
                      {/* @ts-expect-error props */}
                      <IconButton
                        aria-label="Delete"
                        schema="tertiary"
                        onClick={() => setDeleteTemplate(template)}
                      >
                        <TrashCanSolidV6 />
                      </IconButton>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      )}

      {templates.length > 0 && (
        <TemplatesPagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onGoToPage={(nextPage) => updateParams({ page: nextPage })}
        />
      )}

      <TextTemplateModal
        template={editTemplate}
        onClose={() => setEditTemplate(null)}
        onSaved={() => {
          setEditTemplate(null);
          refresh();
        }}
      />
      <DeleteTextTemplateModal
        template={deleteTemplate}
        onClose={() => setDeleteTemplate(null)}
        onDeleted={onDeleted}
      />
    </div>
  );
};

export default TextTemplates;
